// Which inboxes a project draws from, and adding or removing one.
//
// Mirrors /projects/mailboxes/{list,connect,disconnect}
// (src/inrange_agents/lambda_handlers/api/ingestion/mailbox_*.py). ONE request
// returns every inbox with everything the card renders - never a hook per row.
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { queryKeys } from "../queryKeys";

export interface ProjectMailbox {
  mailbox_id: string;
  account_email: string;
  authorized_user_email: string;
  status: string;
  // Whether the viewer is the person whose OAuth grant this inbox runs on.
  authorized_by_viewer: boolean;
  last_swept_at: string | null;
  linked_at: string;
  linked_by_email: string | null;
  // "pending" / "expanding" while the cold start over this inbox's history for
  // this project is still running, "completed" when it is done, null for a pair
  // that predates the cold-start record.
  cold_start_state: string | null;
}

interface MailboxListResponse {
  project_id: string;
  mailboxes: ProjectMailbox[];
}

export const useProjectMailboxes = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<MailboxListResponse>(
    queryKeys.projectMailboxes(projectId || null),
    () =>
      http<MailboxListResponse>("/projects/mailboxes/list", {
        project_id: projectId,
      }),
    { enabled: Boolean(projectId), refetchOnWindowFocus: false },
  );
};

export const useConnectProjectMailbox = (projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  // No account in the body, deliberately: the backend links the Google account
  // the CALLER authorised, so nobody can add an inbox whose grant they do not
  // hold.
  return useMutation<MailboxListResponse, Error, void>(
    () =>
      http<MailboxListResponse>("/projects/mailboxes/connect", {
        project_id: projectId,
      }),
    {
      onSuccess: () => {
        void qc.invalidateQueries(queryKeys.projectMailboxes(projectId));
      },
    },
  );
};

export const useDisconnectProjectMailbox = (projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<MailboxListResponse, Error, { accountEmail: string }>(
    ({ accountEmail }) =>
      http<MailboxListResponse>("/projects/mailboxes/disconnect", {
        project_id: projectId,
        account_email: accountEmail,
      }),
    {
      onSuccess: () => {
        void qc.invalidateQueries(queryKeys.projectMailboxes(projectId));
      },
    },
  );
};

// The result of asking for a sweep now, per inbox. A skip is REPORTED rather
// than swallowed: each inbox comes back either staged or with words saying what
// will clear the skip.
export interface SweepNowRow {
  account_email: string;
  staged: boolean;
  // Present only on a staged row. The sweep reports fine-grained progress
  // against it, which is what lets the card show what the sweep is doing rather
  // than only that it started.
  operation_key: string | null;
  skip_reason: string | null;
  message: string;
}

export interface SweepNowResponse {
  project_id: string;
  mailboxes: SweepNowRow[];
  staged_count: number;
}

export const useSweepProjectMailboxesNow = (projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<SweepNowResponse, Error, void>(
    () =>
      http<SweepNowResponse>("/projects/mailboxes/sweep-now", {
        project_id: projectId,
      }),
    {
      onSuccess: () => {
        // The sweep runs asynchronously on the mailbox's lane, so this refetch
        // shows the staging, not the result - last_swept_at moves when the
        // segment finishes, which is why the card says "queued", not "done".
        void qc.invalidateQueries(queryKeys.projectMailboxes(projectId));
      },
    },
  );
};
